import { css } from '@emotion/react';
import {
  Colors,
  Field,
  Link,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { Button } from '@zapier/marketing-ui';
import { addParamsToUrl } from '@zapier/url-utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';
import { parseHash } from '@zapier/auth-pages/src/components/pages/ResetPasswordPage/parseHash';
import { useResetPassword } from './useResetPassword';

const errorStyle = css`
  ${Typography.base}
  color: ${Colors.StatusErrorWeaker};
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.StatusSuccessWeaker};
`;

export function ResetPasswordPage() {
  const size = useSize();
  const router = useRouter();
  const t = useTranslations('reset-password');

  const hashPart = router.asPath.split('#')?.[1];
  const hash = (hashPart || router.query?.hash || '').toString();
  const { username } = parseHash(hash);

  const { canSubmit, error, formData, handleChange, handleSubmit, status } =
    useResetPassword({ hash });

  const title = username
    ? t('title-with-username', { username })
    : t('title-default');

  function LoginLink(chunks: React.ReactNode) {
    return (
      <Link href={addParamsToUrl(urls.LOGIN_URL, { email: username })}>
        {chunks}
      </Link>
    );
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={title}
      titlePosition={'right'}
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        <Field
          label={t('password-label')}
          inputId="reset-password-1"
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="new-password"
              isDisabled={status === 'submitting'}
              name="password1"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('password1', e.target.value)
              }
              placeholder={t('password-placeholder')}
              type="password"
              value={formData.password1}
            />
          )}
        />

        <Field
          label={t('confirm-password-label')}
          inputId="reset-password-2"
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="new-password"
              isDisabled={status === 'submitting'}
              name="password2"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('password2', e.target.value)
              }
              placeholder={t('confirm-password-placeholder')}
              type="password"
              value={formData.password2}
            />
          )}
        />
        <Button
          type="submit"
          variant="brand"
          fullWidth={true}
          disabled={!canSubmit || status !== 'initial'}
        >
          {t('submit-button')}
        </Button>
        {error && <div css={errorStyle}>{error}</div>}
        {status === 'success' && (
          <div css={successStyle}>{t.rich('success', { link: LoginLink })}</div>
        )}
      </form>
    </AuthLayout>
  );
}
